/**
 * Confirmation card for transaction confirmations
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { TransactionConfirmation } from '../../types/chat';

interface ConfirmationCardProps {
  confirmation: TransactionConfirmation;
  onConfirm?: () => void;
  onCancel?: () => void;
  isConfirming?: boolean;
}

const ConfirmationCard: React.FC<ConfirmationCardProps> = ({
  confirmation,
  onConfirm,
  onCancel,
  isConfirming = false,
}) => {
  const getTypeColor = () => {
    switch (confirmation.type) {
      case 'entry':
        return 'success';
      case 'exit':
        return 'error';
      case 'adjustment':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeIcon = () => {
    switch (confirmation.type) {
      case 'entry':
        return <TrendingUpIcon fontSize="small" />;
      case 'exit':
        return <TrendingDownIcon fontSize="small" />;
      case 'adjustment':
        return <EditIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatPercent = (value: number | undefined) => {
    if (value === undefined) return '-';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const renderEntryDetails = () => (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Quantity
        </Typography>
        <Typography variant="body2" fontWeight="medium">
          {confirmation.quantity} shares
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Entry Price
        </Typography>
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(confirmation.entryPrice)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Stop Loss
        </Typography>
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(confirmation.stopLoss)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Target Price
        </Typography>
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(confirmation.targetPrice)}
        </Typography>
      </Box>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          Total Cost
        </Typography>
        <Typography variant="body2" fontWeight="bold">
          {formatCurrency((confirmation.quantity || 0) * (confirmation.entryPrice || 0))}
        </Typography>
      </Box>
    </>
  );

  const renderExitDetails = () => (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Quantity
        </Typography>
        <Typography variant="body2" fontWeight="medium">
          {confirmation.currentQuantity} shares
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Exit Price
        </Typography>
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(confirmation.exitPrice)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Entry Price
        </Typography>
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(confirmation.entryPriceOriginal)}
        </Typography>
      </Box>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          Estimated Gain/Loss
        </Typography>
        <Typography
          variant="body2"
          fontWeight="bold"
          color={
            (confirmation.estimatedGainLoss || 0) >= 0
              ? 'success.main'
              : 'error.main'
          }
        >
          {formatCurrency(confirmation.estimatedGainLoss)} (
          {formatPercent(confirmation.estimatedGainLossPercent)})
        </Typography>
      </Box>
    </>
  );

  const renderAdjustmentDetails = () => (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Adjustment Type
        </Typography>
        <Typography variant="body2" fontWeight="medium">
          {confirmation.adjustmentType === 'stop_loss'
            ? 'Stop Loss'
            : 'Target Price'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Old Value
        </Typography>
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(confirmation.oldValue)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          New Value
        </Typography>
        <Typography variant="body2" fontWeight="bold" color="primary.main">
          {formatCurrency(confirmation.newValue)}
        </Typography>
      </Box>
    </>
  );

  return (
    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Chip
            icon={getTypeIcon() || undefined}
            label={confirmation.type.toUpperCase()}
            color={getTypeColor()}
            size="small"
          />
          <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
            {confirmation.symbol}
          </Typography>
        </Box>

        {/* Portfolio */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Portfolio: {confirmation.portfolioName}
        </Typography>

        {/* Details */}
        {confirmation.type === 'entry' && renderEntryDetails()}
        {confirmation.type === 'exit' && renderExitDetails()}
        {confirmation.type === 'adjustment' && renderAdjustmentDetails()}

        {/* Actions */}
        {(onConfirm || onCancel) && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            {onCancel && (
              <Button
                variant="outlined"
                size="small"
                onClick={onCancel}
                disabled={isConfirming}
                sx={{ flex: 1 }}
              >
                Cancel
              </Button>
            )}
            {onConfirm && (
              <Button
                variant="contained"
                size="small"
                onClick={onConfirm}
                disabled={isConfirming}
                color={confirmation.type === 'exit' ? 'error' : 'primary'}
                sx={{ flex: 1 }}
              >
                {isConfirming ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  `Confirm ${confirmation.type.charAt(0).toUpperCase() + confirmation.type.slice(1)}`
                )}
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ConfirmationCard;
